<script setup lang="ts">
	import LoginDialog from "@/components/LoginDialog.vue";
	import { useSettings } from "@/composables/settings";
	import router from "@/router";
	import {
		isUserFlightPathAdmin,
		setCustomerId,
		useCustomerId,
		setOrganizationId,
	} from "@ptx-autonomous/libs/api";
	import { useMap } from "@ptx-autonomous/libs/maplibre";
	import { useQueryClient } from "@tanstack/vue-query";
	import { destination as turfDestination } from "@turf/destination";
	import { LngLat } from "maplibre-gl";
	import { onMounted, onUnmounted, ref } from "vue";

	// Screensaver panning state
	const isPanningActive = ref(false);
	const panningInterval = ref<number>();
	const currentPanTarget = ref<"start" | "end">("end");
	const { map } = useMap();
	const { organizationId } = useSettings();
	const queryClient = useQueryClient();

	onMounted(() => {
		queryClient.clear();
		startScreensaverPanning();
	});

	onUnmounted(() => {
		stopScreensaverPanning();
	});

	function startScreensaverPanning() {
		if (!map || isPanningActive.value) return;

		isPanningActive.value = true;
		const startPoint = map.getCenter();
		const endPoint = turfDestination(
			[startPoint.lng, startPoint.lat],
			1000000, // 1000 km distance
			Math.random() * 360, // Random bearing
			{ units: "meters" },
		).geometry.coordinates;

		const panToPoint = (target: typeof startPoint, duration: number) => {
			if (!map || !isPanningActive.value) return;
			const zoom = 13;
			map.easeTo({
				center: [target.lng, target.lat],
				zoom,
				duration,
				essential: true,
			});
		};

		// Start the initial pan
		currentPanTarget.value = "end";
		panToPoint(new LngLat(endPoint[0], endPoint[1]), 3 * 60 * 60 * 1000);
	}

	function stopScreensaverPanning() {
		isPanningActive.value = false;
		if (panningInterval.value) {
			clearInterval(panningInterval.value);
			panningInterval.value = undefined;
		}
		map?.stop();
	}

	async function handleLoginSuccess() {
		if (!(await isUserFlightPathAdmin())) {
			await setCustomerId(false);
		} else {
			await setCustomerId(false);
			organizationId.value = useCustomerId(); // Save the organizationId
			await setOrganizationId(organizationId.value);
		}
		router.push({ name: "home" });
	}
</script>

<template>
	<LoginDialog @login-success="handleLoginSuccess" />
</template>
